import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface TrabajoData {
	nombreComitente: string
	actividadComitente: string
	descripcionTrabajo: string
	gruasMaquinarias: string
	duracionEstimada: string
	direccionSitio: string
	dentroFueraPredio: string
	descripcionSitio: string
}

interface RCContratistasTrabajoScreenProps {
	onNext: (data: TrabajoData) => void
	onBack: () => void
	data?: Partial<TrabajoData>
}

export function RCContratistasTrabajoScreen({
	onNext,
	onBack,
	data,
}: RCContratistasTrabajoScreenProps) {
	const [formData, setFormData] = useState<TrabajoData>({
		nombreComitente: data?.nombreComitente || '',
		actividadComitente: data?.actividadComitente || '',
		descripcionTrabajo: data?.descripcionTrabajo || '',
		gruasMaquinarias: data?.gruasMaquinarias || '',
		duracionEstimada: data?.duracionEstimada || '',
		direccionSitio: data?.direccionSitio || '',
		dentroFueraPredio: data?.dentroFueraPredio || '',
		descripcionSitio: data?.descripcionSitio || '',
	})

	const [errors, setErrors] = useState<
		Partial<Record<keyof TrabajoData, string>>
	>({})

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof TrabajoData, string>> = {}

		if (!formData.nombreComitente.trim()) {
			newErrors.nombreComitente = 'El nombre del comitente es requerido'
		}
		if (!formData.descripcionTrabajo.trim()) {
			newErrors.descripcionTrabajo = 'La descripción del trabajo es requerida'
		}
		if (!formData.duracionEstimada.trim()) {
			newErrors.duracionEstimada = 'La duración estimada es requerida'
		}
		if (!formData.direccionSitio.trim()) {
			newErrors.direccionSitio = 'La dirección del sitio es requerida'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = () => {
		if (validateForm()) {
			onNext(formData)
		}
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Información del Trabajo
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Detallá el trabajo a realizar y el sitio de la obra
					</p>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Nombre del Comitente"
							labelPosition="internal"
							size="l"
							placeholder="Nombre de quien contrata el trabajo"
							value={formData.nombreComitente}
							onChange={(e) =>
								setFormData({ ...formData, nombreComitente: e.target.value })
							}
							errorText={errors.nombreComitente}
						/>

						<InputText
							label="Ocupación o Actividad del Comitente (opcional)"
							labelPosition="internal"
							size="l"
							placeholder="Ej: Desarrollador inmobiliario"
							value={formData.actividadComitente}
							onChange={(e) =>
								setFormData({ ...formData, actividadComitente: e.target.value })
							}
						/>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Descripción del Trabajo a Realizar
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={4}
								placeholder="Describí detalladamente el trabajo que se va a realizar"
								value={formData.descripcionTrabajo}
								onChange={(e) =>
									setFormData({
										...formData,
										descripcionTrabajo: e.target.value,
									})
								}
							/>
							{errors.descripcionTrabajo && (
								<p className="text-sm text-red-500">
									{errors.descripcionTrabajo}
								</p>
							)}
						</div>

						<InputText
							label="Grúas o Maquinarias de Gran Porte (opcional)"
							labelPosition="internal"
							size="l"
							placeholder="Ej: Grúa torre, excavadora, etc."
							value={formData.gruasMaquinarias}
							onChange={(e) =>
								setFormData({ ...formData, gruasMaquinarias: e.target.value })
							}
						/>

						<InputText
							label="Duración Estimada del Trabajo"
							labelPosition="internal"
							size="l"
							placeholder="Ej: 6 meses"
							value={formData.duracionEstimada}
							onChange={(e) =>
								setFormData({ ...formData, duracionEstimada: e.target.value })
							}
							errorText={errors.duracionEstimada}
						/>

						<InputText
							label="Dirección Exacta del Sitio"
							labelPosition="internal"
							size="l"
							placeholder="Dirección completa de la obra"
							value={formData.direccionSitio}
							onChange={(e) =>
								setFormData({ ...formData, direccionSitio: e.target.value })
							}
							errorText={errors.direccionSitio}
						/>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								¿Dentro o Fuera del Predio del Comitente? (opcional)
							</label>
							<select
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								value={formData.dentroFueraPredio}
								onChange={(e) =>
									setFormData({
										...formData,
										dentroFueraPredio: e.target.value,
									})
								}
							>
								<option value="">Seleccionar...</option>
								<option value="dentro">Dentro del predio</option>
								<option value="fuera">Fuera del predio</option>
								<option value="ambos">Ambos</option>
							</select>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Descripción del Sitio (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Características del sitio donde se realizará el trabajo"
								value={formData.descripcionSitio}
								onChange={(e) =>
									setFormData({ ...formData, descripcionSitio: e.target.value })
								}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
